import {
  AfterViewInit,
  DestroyRef,
  Directive,
  ElementRef,
  Host,
  InjectionToken,
  Optional,
  inject
} from '@angular/core';
import { fromEvent, map, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  InputSearchComponent,
  PasswordComponent,
  PhoneNumberComponent
} from '~ui-kit/inputs';
import { SelectComponent } from '~ui-kit/select';
import { TagsSelectorComponent } from '~ui-kit/tags-selector';

export const INPUT_FIELD = new InjectionToken<InputFieldDirective>(
  'InputFieldDirective'
);

@Directive({
  selector:
    'input[type=text][input-field], input[type=password][input-field], input[type=email][input-field], input[type=tel][input-field], input[type=url][input-field], input[type=number][input-field], input[type=range][input-field], input[type=date][input-field], input[type=month][input-field], input[type=week][input-field], input[type=time][input-field], input[type=datetime-local][input-field], input[type=color][input-field], input[type=file][input-field], div[inputSearch][input-field], select-field[input-field], password-field[input-field], phone-number-field[input-field], textarea[input-field], tags-selector[input-field]',
  standalone: true,
  providers: [
    { provide: INPUT_FIELD, useExisting: InputFieldDirective },
    InputSearchComponent
  ]
})
export class InputFieldDirective implements AfterViewInit {
  private _destroyRef = inject(DestroyRef);

  readonly nativeTagNames = ['INPUT', 'TEXTAREA'];

  constructor(
    private _elementRef: ElementRef,
    @Optional()
    @Host()
    private _inputSearch: InputSearchComponent,
    @Optional()
    @Host()
    private _selectField: SelectComponent,
    @Optional()
    @Host()
    private _passwordField: PasswordComponent,
    @Optional()
    @Host()
    private _phoneNumberField: PhoneNumberComponent,
    @Optional()
    @Host()
    private _tagsSelector: TagsSelectorComponent
  ) {}

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get host(): any {
    return this._elementRef.nativeElement;
  }

  ngAfterViewInit(): void {
    this.host.classList.add('input-field');
    if (this.nativeTagNames.includes(this.host.tagName)) {
      this._handleValueChange();
      this._handleBlur();
      this._handleFocus();
      this._handleDefaultValue();
    }
  }

  clearInput(): void {
    if (this.nativeTagNames.includes(this.host.tagName)) {
      this.host.value = '';
      this.host.classList.remove('input-filled');
      this.host.focus();
      return;
    }

    if (this.host.hasAttribute('inputSearch')) {
      this._inputSearch.clearInput();
      return;
    }

    if (this.host.tagName === 'PASSWORD-FIELD') {
      this._passwordField.clearInput();
      return;
    }

    if (this.host.tagName === 'PHONE-NUMBER-FIELD') {
      this._phoneNumberField.clearInput();
      return;
    }
  }

  focus(): void {
    if (this.nativeTagNames.includes(this.host.tagName)) {
      this.host.focus();
      return;
    }

    if (this.host.hasAttribute('inputSearch')) {
      this._inputSearch.focusInput();
      return;
    }
    if (this.host.tagName === 'SELECT-FIELD') {
      this._selectField.click();
      return;
    }

    if (this.host.tagName === 'PASSWORD-FIELD') {
      this._passwordField.focusInput();
      return;
    }

    if (this.host.tagName === 'PHONE-NUMBER-FIELD') {
      this._phoneNumberField.focusInput();
      return;
    }

    if (this.host.tagName === 'TAGS-SELECTOR') {
      this._tagsSelector.click();
      return;
    }
  }

  private _handleValueChange(): void {
    fromEvent(this.host as HTMLInputElement, 'input')
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        map(e => (e.target as HTMLInputElement).value),
        tap(value => {
          value
            ? this.host.classList.add('input-filled')
            : this.host.classList.remove('input-filled');
        })
      )
      .subscribe();
  }

  private _handleFocus(): void {
    fromEvent(this.host, 'focus')
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap(() => {
          this.host.classList.add('input-focused');
        })
      )
      .subscribe();
  }

  private _handleBlur(): void {
    fromEvent(this.host, 'blur')
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap(() => {
          this.host.classList.remove('input-focused');
        })
      )
      .subscribe();
  }

  private _handleDefaultValue(): void {
    this.host.value
      ? this.host.classList.add('input-filled')
      : this.host.classList.remove('input-filled');
  }
}
